import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";

export default function SmoothLoadTransition({
  loading,
  children,
  minSpinnerMs = 420,
  fadeMs = 220,
  minHeight = 220,
  spinnerSize = 30,
  loaderSx,
  contentSx,
}) {
  const [showContent, setShowContent] = useState(!loading);
  const [showLoader, setShowLoader] = useState(loading);
  const [loaderOpacity, setLoaderOpacity] = useState(loading ? 1 : 0);
  const loadingStartedAtRef = useRef(loading ? Date.now() : 0);

  useEffect(() => {
    let revealTimer;
    let hideTimer;

    if (loading) {
      loadingStartedAtRef.current = Date.now();
      setShowContent(false);
      setShowLoader(true);
      setLoaderOpacity(1);

      return () => {
        clearTimeout(revealTimer);
        clearTimeout(hideTimer);
      };
    }

    const elapsed = loadingStartedAtRef.current
      ? Date.now() - loadingStartedAtRef.current
      : minSpinnerMs;
    const remaining = Math.max(0, minSpinnerMs - elapsed);

    revealTimer = setTimeout(() => {
      setShowContent(true);
      setLoaderOpacity(0);
      hideTimer = setTimeout(() => setShowLoader(false), fadeMs);
    }, remaining);

    return () => {
      clearTimeout(revealTimer);
      clearTimeout(hideTimer);
    };
  }, [fadeMs, loading, minSpinnerMs]);

  return (
    <Box sx={{ position: "relative", minHeight }}>
      <Box
        sx={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? "translateY(0)" : "translateY(10px)",
          transition: `opacity ${fadeMs}ms ease, transform ${fadeMs}ms ease`,
          ...contentSx,
        }}
      >
        {children}
      </Box>

      {showLoader && (
        <Box
          sx={{
            position: showContent ? "absolute" : "relative",
            inset: showContent ? 0 : "auto",
            minHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: loaderOpacity,
            transition: `opacity ${fadeMs}ms ease`,
            pointerEvents: "none",
            zIndex: 1,
            ...loaderSx,
          }}
        >
          <CircularProgress size={spinnerSize} />
        </Box>
      )}
    </Box>
  );
}
